import FeatureAction from './FeatureAction';
import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Cloud,
  CreditCard,
  Database,
  Key,
  ListChecks,
  Lock,
  LucideIcon,
  Play,
  Radio,
  Search,
  Terminal,
  User,
  Webhook,
  Workflow,
} from 'lucide-react';
import { LockedFeatureMap } from '../navigation/featureAccess';
import { TabType } from '../navigation/tabRoutes';
import { safeLocalStorageGet, safeLocalStorageSet } from '../utils/safeStorage';
import './Sidebar.css';

interface SidebarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  lockedTabs?: LockedFeatureMap;
}

interface TabItem {
  id: TabType;
  label: string;
  icon: LucideIcon;
  description: string;
}

interface AuxiliaryTabGroup {
  ariaLabel: string;
  tabs: TabItem[];
}

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebarCollapsed';

const PRIMARY_TABS: TabItem[] = [
  {
    id: 'playground',
    label: 'Playground',
    icon: Play,
    description: 'Upload & analyze videos',
  },
  {
    id: 'index',
    label: 'Index',
    icon: Database,
    description: 'Manage video indexes',
  },
  {
    id: 'workflows',
    label: 'Execute',
    icon: Workflow,
    description: 'Run engines across indexes',
  },
  {
    id: 'search',
    label: 'Search',
    icon: Search,
    description: 'Search videos by content',
  },
  {
    id: 'realtime',
    label: 'Realtime',
    icon: Radio,
    description: 'Process a live camera feed',
  },
];

const AUXILIARY_TAB_GROUPS: AuxiliaryTabGroup[] = [
  {
    ariaLabel: 'Integration',
    tabs: [
      {
        id: 'connectors',
        label: 'Connectors',
        icon: Cloud,
        description: 'Cloud storage connectors',
      },
      {
        id: 'apikeys',
        label: 'API Key',
        icon: Key,
        description: 'Manage API keys',
      },
      {
        id: 'mcp',
        label: 'MCP',
        icon: Terminal,
        description: 'AI assistant integration',
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        icon: Webhook,
        description: 'Manage webhook endpoints',
      },
      {
        id: 'jobs',
        label: 'Jobs',
        icon: ListChecks,
        description: 'Workflow/import/export jobs',
      },
    ],
  },
  {
    ariaLabel: 'Administrative',
    tabs: [
      {
        id: 'usage',
        label: 'Usage',
        icon: BarChart3,
        description: 'View usage metrics',
      },
      {
        id: 'billing',
        label: 'Billing',
        icon: CreditCard,
        description: 'Credits & payments',
      },
      {
        id: 'profile',
        label: 'Profile',
        icon: User,
        description: 'Profile & settings',
      },
    ],
  },
];

const getInitialCollapsedState = (): boolean => (
  safeLocalStorageGet(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true'
);

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange, lockedTabs = {} }) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(getInitialCollapsedState);

  useEffect(() => {
    safeLocalStorageSet(SIDEBAR_COLLAPSED_STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const renderTab = (tab: TabItem, compact: boolean) => {
    const Icon = tab.icon;
    const lockedFeature = lockedTabs[tab.id];
    const isActive = activeTab === tab.id;
    let title: string | undefined;
    if (lockedFeature) {
      title = `${tab.label}: ${lockedFeature.reason}`;
    } else if (isCollapsed) {
      title = tab.label;
    } else if (compact) {
      title = tab.description;
    }

    return (
      <FeatureAction key={tab.id} feature={lockedFeature?.featureKey || null}>
      <button
        key={tab.id}
        type="button"
        className={`sidebar-tab sidebar-tab--${compact ? 'compact' : 'primary'} ${isActive ? 'active' : ''} ${lockedFeature ? 'locked' : ''}`}
        onClick={() => onTabChange(tab.id)}
        title={title}
        aria-label={lockedFeature ? `${tab.label}. ${lockedFeature.reason}` : tab.label}
        aria-current={isActive ? 'page' : undefined}
      >
        <Icon size={compact ? 16 : 20} aria-hidden="true" />
        {!isCollapsed && (
          <div className="sidebar-tab-content">
            <span className="sidebar-tab-label-row">
              <span className="sidebar-tab-label">{tab.label}</span>
              {lockedFeature && <span className="sidebar-tab-plan-badge">Starter</span>}
            </span>
            {!compact && (
              <span className="sidebar-tab-description">{tab.description}</span>
            )}
          </div>
        )}
        {lockedFeature && (
          <Lock
            className="sidebar-tab-lock-icon"
            size={isCollapsed ? 10 : compact ? 12 : 14}
            aria-hidden="true"
          />
        )}
      </button>
      </FeatureAction>
    );
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <nav className="sidebar-nav" aria-label="Application navigation">
        <div className="sidebar-group sidebar-group--primary">
          {!isCollapsed && (
            <div className="sidebar-group-header">
              <span className="sidebar-group-name">Core</span>
            </div>
          )}
          <div className="sidebar-group-tabs">
            {PRIMARY_TABS.map((tab) => renderTab(tab, false))}
          </div>
        </div>

        <div className="sidebar-auxiliary-groups">
          {AUXILIARY_TAB_GROUPS.map((group) => (
            <div
              key={group.ariaLabel}
              className="sidebar-group sidebar-group--compact"
              role="group"
              aria-label={group.ariaLabel}
            >
              <div className="sidebar-group-header sidebar-group-header--compact">
                {!isCollapsed && (
                  <span className="sidebar-group-name">{group.ariaLabel}</span>
                )}
              </div>
              <div className="sidebar-group-tabs">
                {group.tabs.map((tab) => renderTab(tab, true))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      <div className="sidebar-navigation-footer">
        <button
          type="button"
          className="sidebar-collapse-button"
          onClick={() => setIsCollapsed((collapsed) => !collapsed)}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
